// The one access rule for a subscription. Applied by the auth service at the
// OIDC authorize step (every OIDC client) and by the
// Account SPA's protected route, so both surfaces agree by construction.

export interface SubscriptionFields {
  role?: unknown
  stripe_subscription_id?: unknown
  subscription_status?: unknown
  trial_ends_at?: unknown
}

const DAY_MS = 86_400_000

function trialEnd(user: SubscriptionFields): Date | null {
  const raw = user.trial_ends_at
  let end: Date | null = null
  if (raw instanceof Date) {
    end = raw
  } else if (typeof raw === 'string') {
    end = new Date(raw)
  }
  // A value that does not parse is treated as "no end date", which denies:
  // never grant access on garbage.
  return end && !Number.isNaN(end.getTime()) ? end : null
}

export function hasAccess(user: SubscriptionFields, now: Date = new Date()): boolean {
  if (user.role === 'admin') {
    return true
  }
  switch (user.subscription_status) {
    case 'active':
    case 'past_due':
      return true
    case 'trialing': {
      const end = trialEnd(user)
      return end !== null && end.getTime() > now.getTime()
    }
    default:
      return false
  }
}

// A subscriber is someone with a Stripe subscription stored. Accounts
// backfilled before the paid plan are `active` with none: they keep access
// (hasAccess) but have nothing to manage and can still subscribe.
export function isSubscriber(user: SubscriptionFields): boolean {
  return (
    (user.subscription_status === 'active' || user.subscription_status === 'past_due') &&
    typeof user.stripe_subscription_id === 'string' &&
    user.stripe_subscription_id.length > 0
  )
}

// A checkout whose first payment has not settled (SEPA, a declined card that
// is being retried): the subscription is linked but grants nothing yet, and
// the status still shows the trial or early access it started from. There is
// nothing left to buy. A canceled subscription keeps its old id and is not
// pending; a returning subscriber's pending checkout is recorded as `none` by
// the webhook so it is.
export function hasPendingSubscription(user: SubscriptionFields): boolean {
  return (
    typeof user.stripe_subscription_id === 'string' &&
    user.stripe_subscription_id.length > 0 &&
    user.subscription_status !== 'canceled' &&
    !isSubscriber(user)
  )
}

export function trialDaysLeft(user: SubscriptionFields, now: Date = new Date()): number | null {
  if (user.subscription_status !== 'trialing') {
    return null
  }
  const end = trialEnd(user)
  return end ? Math.max(0, Math.ceil((end.getTime() - now.getTime()) / DAY_MS)) : null
}
